import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart3, Users, ArrowLeft, Loader2, CheckCircle2, Euro, TrendingUp, Percent, UserCheck, Wallet, Banknote, Compass } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
    { value: 'today', label: "Aujourd'hui" },
    { value: 'yesterday', label: 'Hier' },
    { value: 'this_week', label: 'Cette semaine' },
    { value: 'last_week', label: 'Semaine dernière' },
    { value: 'this_month', label: 'Ce mois-ci' },
    { value: 'last_month', label: 'Mois dernier' },
    { value: 'custom', label: 'Personnalisé' },
];

// Couleurs alignees avec la charte de l'app (et palette prospect)
const COLORS = [
    '#2e7cd6', '#60a5fa', '#10b981', '#f59e0b', '#ef4444',
    '#8b5cf6', '#ec4899', '#14b8a6', '#a855f7', '#eab308',
    '#06b6d4', '#f97316', '#64748b',
];

// Couleurs specifiques par statut (override COLORS pour ces cles)
const STATUT_COLORS = {
    visite_virtuelle: '#166534',   // Vert fonce
    visite_presentielle: '#a3e635', // Vert pistache
};

const colorFor = (key, idx) => STATUT_COLORS[key] || COLORS[idx % COLORS.length];

export default function StatistiquesPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeCard, setActiveCard] = useState(null); // 'prospects' | 'regagnes' | 'prestations' | 'taux' | 'commercial' | 'ca' | 'caComm' | 'caSource' | null
    const [period, setPeriod] = useState('today');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [caYear, setCaYear] = useState(new Date().getFullYear());
    const [caCommYear, setCaCommYear] = useState(new Date().getFullYear());
    // CA par source : par défaut, mois et année en cours (clamp >= mai 2026)
    const _now = new Date();
    const _initialY = _now.getFullYear();
    const _initialM = _now.getMonth() + 1; // 1..12
    const [caSrcYear, setCaSrcYear] = useState(_initialY < 2026 || (_initialY === 2026 && _initialM < 5) ? 2026 : _initialY);
    const [caSrcMonth, setCaSrcMonth] = useState(_initialY < 2026 || (_initialY === 2026 && _initialM < 5) ? 5 : _initialM);
    const [data, setData] = useState(null);
    const [prestationsData, setPrestationsData] = useState(null);
    const [regagnesData, setRegagnesData] = useState(null);
    const [tauxData, setTauxData] = useState(null);
    const [commercialData, setCommercialData] = useState(null);
    const [caData, setCaData] = useState(null);
    const [caCommData, setCaCommData] = useState(null);
    const [caSrcData, setCaSrcData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Garde admin uniquement
    useEffect(() => {
        if (user && user.role !== 'admin') {
            navigate('/');
        }
    }, [user, navigate]);

    const fetchProspectsStats = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ period });
            if (period === 'custom') {
                if (!dateDebut || !dateFin) {
                    setLoading(false);
                    return;
                }
                params.append('date_debut', dateDebut);
                params.append('date_fin', dateFin);
            }
            const { data } = await axios.get(`${API}/api/stats/prospects?${params.toString()}`, { withCredentials: true });
            setData(data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchPrestationsStats = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ period });
            if (period === 'custom') {
                if (!dateDebut || !dateFin) {
                    setLoading(false);
                    return;
                }
                params.append('date_debut', dateDebut);
                params.append('date_fin', dateFin);
            }
            const { data } = await axios.get(`${API}/api/stats/prestations-acceptes?${params.toString()}`, { withCredentials: true });
            setPrestationsData(data);
        } catch {
            setPrestationsData(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchRegagnesStats = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ period });
            if (period === 'custom') {
                if (!dateDebut || !dateFin) {
                    setLoading(false);
                    return;
                }
                params.append('date_debut', dateDebut);
                params.append('date_fin', dateFin);
            }
            const { data } = await axios.get(`${API}/api/stats/prospects-regagnes?${params.toString()}`, { withCredentials: true });
            setRegagnesData(data);
        } catch {
            setRegagnesData(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchTauxStats = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ period });
            if (period === 'custom') {
                if (!dateDebut || !dateFin) {
                    setLoading(false);
                    return;
                }
                params.append('date_debut', dateDebut);
                params.append('date_fin', dateFin);
            }
            const { data } = await axios.get(`${API}/api/stats/taux-conversion?${params.toString()}`, { withCredentials: true });
            setTauxData(data);
        } catch {
            setTauxData(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchCommercialStats = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ period });
            if (period === 'custom') {
                if (!dateDebut || !dateFin) {
                    setLoading(false);
                    return;
                }
                params.append('date_debut', dateDebut);
                params.append('date_fin', dateFin);
            }
            const { data } = await axios.get(`${API}/api/stats/conversion-commercial?${params.toString()}`, { withCredentials: true });
            setCommercialData(data);
        } catch {
            setCommercialData(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchCaStats = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/stats/chiffre-affaires?year=${caYear}`, { withCredentials: true });
            setCaData(data);
        } catch {
            setCaData(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchCaCommStats = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/stats/chiffre-affaires-commercial?year=${caCommYear}`, { withCredentials: true });
            setCaCommData(data);
        } catch {
            setCaCommData(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchCaSrcStats = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/stats/chiffre-affaires-source?year=${caSrcYear}&month=${caSrcMonth}`, { withCredentials: true });
            setCaSrcData(data);
        } catch {
            setCaSrcData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeCard === 'prospects') {
            if (period !== 'custom' || (dateDebut && dateFin)) {
                fetchProspectsStats();
            }
        } else if (activeCard === 'prestations') {
            if (period !== 'custom' || (dateDebut && dateFin)) {
                fetchPrestationsStats();
            }
        } else if (activeCard === 'regagnes') {
            if (period !== 'custom' || (dateDebut && dateFin)) {
                fetchRegagnesStats();
            }
        } else if (activeCard === 'taux') {
            if (period !== 'custom' || (dateDebut && dateFin)) {
                fetchTauxStats();
            }
        } else if (activeCard === 'commercial') {
            if (period !== 'custom' || (dateDebut && dateFin)) {
                fetchCommercialStats();
            }
        } else if (activeCard === 'ca') {
            fetchCaStats();
        } else if (activeCard === 'caComm') {
            fetchCaCommStats();
        } else if (activeCard === 'caSource') {
            fetchCaSrcStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCard, period, dateDebut, dateFin, caYear, caCommYear, caSrcYear, caSrcMonth]);

    return (
        <div className="container mx-auto p-6 max-w-7xl" data-testid="stats-page">
            <div className="flex items-center gap-3 mb-6">
                {activeCard && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setActiveCard(null); setData(null); }}
                        data-testid="stats-back-btn"
                    >
                        <ArrowLeft size={16} className="mr-1.5" />
                        Retour
                    </Button>
                )}
                <BarChart3 size={28} className="text-[#2e7cd6]" />
                <h1 className="text-2xl font-bold text-slate-800">Statistiques</h1>
            </div>

            {!activeCard && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Card
                        className="rounded-xl border border-slate-200 shadow-sm bg-white cursor-pointer transition-all hover:shadow-md hover:border-[#2e7cd6]"
                        onClick={() => setActiveCard('prospects')}
                        data-testid="stats-card-prospects"
                    >
                        <CardContent className="p-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                                <Users size={22} className="text-[#2e7cd6]" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-slate-800">Stats Prospect</p>
                                <p className="text-sm text-slate-500 mt-1">Prospects créés par statut · période au choix</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card
                        className="rounded-xl border border-slate-200 shadow-sm bg-white cursor-pointer transition-all hover:shadow-md hover:border-amber-500"
                        onClick={() => setActiveCard('regagnes')}
                        data-testid="stats-card-regagnes"
                    >
                        <CardContent className="p-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                                <TrendingUp size={22} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-slate-800">Prospects regagnés</p>
                                <p className="text-sm text-slate-500 mt-1">"Sans réponse" → "Visite" · période au choix</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card
                        className="rounded-xl border border-slate-200 shadow-sm bg-white cursor-pointer transition-all hover:shadow-md hover:border-emerald-500"
                        onClick={() => setActiveCard('prestations')}
                        data-testid="stats-card-prestations"
                    >
                        <CardContent className="p-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                                <CheckCircle2 size={22} className="text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-slate-800">Prestations acceptées</p>
                                <p className="text-sm text-slate-500 mt-1">Devis passés en "Accepté" · montant TTC</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card
                        className="rounded-xl border border-slate-200 shadow-sm bg-white cursor-pointer transition-all hover:shadow-md hover:border-violet-500"
                        onClick={() => setActiveCard('taux')}
                        data-testid="stats-card-taux"
                    >
                        <CardContent className="p-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                                <Percent size={22} className="text-violet-600" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-slate-800">Taux de conversion</p>
                                <p className="text-sm text-slate-500 mt-1">Dossiers convertis / Dossiers avec devis</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card
                        className="rounded-xl border border-slate-200 shadow-sm bg-white cursor-pointer transition-all hover:shadow-md hover:border-orange-500"
                        onClick={() => setActiveCard('commercial')}
                        data-testid="stats-card-commercial"
                    >
                        <CardContent className="p-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                                <UserCheck size={22} className="text-orange-600" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-slate-800">Conversion chaque commercial</p>
                                <p className="text-sm text-slate-500 mt-1">Taux de conversion ventilé par commercial</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card
                        className="rounded-xl border border-slate-200 shadow-sm bg-white cursor-pointer transition-all hover:shadow-md hover:border-emerald-600"
                        onClick={() => setActiveCard('ca')}
                        data-testid="stats-card-ca"
                    >
                        <CardContent className="p-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                                <Wallet size={22} className="text-emerald-700" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-slate-800">Chiffre d'affaires</p>
                                <p className="text-sm text-slate-500 mt-1">CA des prestations programmées sur la période</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card
                        className="rounded-xl border border-slate-200 shadow-sm bg-white cursor-pointer transition-all hover:shadow-md hover:border-teal-600"
                        onClick={() => setActiveCard('caComm')}
                        data-testid="stats-card-ca-comm"
                    >
                        <CardContent className="p-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                                <Banknote size={22} className="text-teal-700" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-slate-800">CA par commercial</p>
                                <p className="text-sm text-slate-500 mt-1">CA mensuel ventilé par commercial</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card
                        className="rounded-xl border border-slate-200 shadow-sm bg-white cursor-pointer transition-all hover:shadow-md hover:border-indigo-500"
                        onClick={() => setActiveCard('caSource')}
                        data-testid="stats-card-ca-source"
                    >
                        <CardContent className="p-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                                <Compass size={22} className="text-indigo-600" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-slate-800">CA par source</p>
                                <p className="text-sm text-slate-500 mt-1">CA d'un mois donné, ventilé par source d'acquisition</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeCard === 'prospects' && (
                <ProspectsStats
                    period={period}
                    setPeriod={setPeriod}
                    dateDebut={dateDebut}
                    setDateDebut={setDateDebut}
                    dateFin={dateFin}
                    setDateFin={setDateFin}
                    data={data}
                    loading={loading}
                />
            )}

            {activeCard === 'regagnes' && (
                <RegagnesStats
                    period={period}
                    setPeriod={setPeriod}
                    dateDebut={dateDebut}
                    setDateDebut={setDateDebut}
                    dateFin={dateFin}
                    setDateFin={setDateFin}
                    data={regagnesData}
                    loading={loading}
                />
            )}

            {activeCard === 'prestations' && (
                <PrestationsStats
                    period={period}
                    setPeriod={setPeriod}
                    dateDebut={dateDebut}
                    setDateDebut={setDateDebut}
                    dateFin={dateFin}
                    setDateFin={setDateFin}
                    data={prestationsData}
                    loading={loading}
                />
            )}

            {activeCard === 'taux' && (
                <TauxStats
                    period={period}
                    setPeriod={setPeriod}
                    dateDebut={dateDebut}
                    setDateDebut={setDateDebut}
                    dateFin={dateFin}
                    setDateFin={setDateFin}
                    data={tauxData}
                    loading={loading}
                />
            )}

            {activeCard === 'commercial' && (
                <CommercialStats
                    period={period}
                    setPeriod={setPeriod}
                    dateDebut={dateDebut}
                    setDateDebut={setDateDebut}
                    dateFin={dateFin}
                    setDateFin={setDateFin}
                    data={commercialData}
                    loading={loading}
                />
            )}

            {activeCard === 'ca' && (
                <ChiffreAffairesStats
                    year={caYear}
                    setYear={setCaYear}
                    data={caData}
                    loading={loading}
                />
            )}

            {activeCard === 'caComm' && (
                <CaCommercialStats
                    year={caCommYear}
                    setYear={setCaCommYear}
                    data={caCommData}
                    loading={loading}
                />
            )}

            {activeCard === 'caSource' && (
                <CaSourceStats
                    year={caSrcYear}
                    setYear={setCaSrcYear}
                    month={caSrcMonth}
                    setMonth={setCaSrcMonth}
                    data={caSrcData}
                    loading={loading}
                />
            )}
        </div>
    );
}

function PrestationsStats({ period, setPeriod, dateDebut, setDateDebut, dateFin, setDateFin, data, loading }) {
    const count = data?.count || 0;
    const totalTtc = data?.total_ttc || 0;
    const items = data?.items || [];
    const fmtMoney = (v) => (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtDate = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };
    return (
        <Card className="rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="stats-prestations-panel">
            <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <CheckCircle2 size={20} className="text-emerald-600" />
                        <p className="text-lg font-semibold text-slate-800">Prestations acceptées</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-44 h-9 text-sm" data-testid="stats-prest-period-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIOD_OPTIONS.map(o => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {period === 'custom' && (
                            <>
                                <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-prest-date-debut" />
                                <span className="text-slate-400 text-sm">au</span>
                                <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-prest-date-fin" />
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-500">
                        <Loader2 size={20} className="animate-spin mr-2" /> Chargement...
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-emerald-300 flex items-center justify-center">
                                    <CheckCircle2 size={24} className="text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-emerald-700">Devis acceptés</p>
                                    <p className="text-3xl font-bold text-slate-800 leading-none mt-1" data-testid="stats-prestations-count">{count}</p>
                                </div>
                            </div>
                            <div className="rounded-lg border border-[#2e7cd6]/40 bg-blue-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-[#2e7cd6]/60 flex items-center justify-center">
                                    <Euro size={24} className="text-[#2e7cd6]" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-[#2568b8]">Montant TTC total</p>
                                    <p className="text-3xl font-bold text-slate-800 leading-none mt-1" data-testid="stats-prestations-total">
                                        {fmtMoney(totalTtc)} €
                                    </p>
                                </div>
                            </div>
                        </div>
                        {items.length === 0 ? (
                            <p className="text-center text-slate-500 py-10">Aucun devis accepté sur cette période</p>
                        ) : (
                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm" data-testid="stats-prestations-table">
                                    <thead className="bg-slate-50 text-slate-600">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">Client</th>
                                            <th className="text-left px-3 py-2 font-medium">N° devis</th>
                                            <th className="text-left px-3 py-2 font-medium">Formule</th>
                                            <th className="text-right px-3 py-2 font-medium">Montant TTC</th>
                                            <th className="text-left px-3 py-2 font-medium">Accepté le</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map(it => (
                                            <tr key={it.id} className="border-t border-slate-100">
                                                <td className="px-3 py-2 text-slate-800">{it.client_nom || '-'}</td>
                                                <td className="px-3 py-2 text-slate-500">{it.numero || '-'}</td>
                                                <td className="px-3 py-2 text-slate-500 capitalize">{it.formule || '-'}</td>
                                                <td className="px-3 py-2 text-right font-medium text-slate-800">{fmtMoney(it.prix_ttc)} €</td>
                                                <td className="px-3 py-2 text-slate-500">{fmtDate(it.changed_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

const pctOf = (count, total) => (total ? Math.round((count / total) * 100) : 0);

function AssistanteProspectsChart({ assistante }) {
    const items = (assistante.items || []).filter(i => i.count > 0);
    return (
        <div className="mt-8 pt-6 border-t border-slate-200" data-testid={`stats-prospects-assistante-${(assistante.name || '').toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-4">
                <Users size={17} className="text-[#2e7cd6]" />
                <p className="text-base font-semibold text-slate-800">Prospects créés par {assistante.name}</p>
                <span className="ml-1 text-xl font-bold text-[#2e7cd6]">{assistante.total}</span>
            </div>
            {items.length === 0 ? (
                <p className="text-center text-slate-500 py-8 text-sm">Aucun prospect créé par {assistante.name} sur la période choisie</p>
            ) : (
                <>
                    <div className="h-60 sm:h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={items} margin={{ top: 8, right: 16, bottom: 50, left: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis
                                    dataKey="label"
                                    angle={-25}
                                    textAnchor="end"
                                    interval={0}
                                    tick={{ fontSize: 11, fill: '#475569' }}
                                    height={60}
                                />
                                <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
                                <Tooltip
                                    cursor={{ fill: 'rgba(46,124,214,0.06)' }}
                                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                                    formatter={(value) => [`${value} (${pctOf(value, assistante.total)}%)`, 'Nombre']}
                                />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {items.map((entry, idx) => (
                                        <Cell key={entry.key} fill={colorFor(entry.key, idx)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3">
                        {items.map((entry, idx) => (
                            <div key={entry.key} className="flex items-center gap-2 text-sm">
                                <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: colorFor(entry.key, idx) }} />
                                <span className="text-slate-700">{entry.label}</span>
                                <span className="font-semibold text-slate-900">({entry.count} – {pctOf(entry.count, assistante.total)}%)</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function SourceProspectsChart({ source }) {
    const items = (source.items || []).filter(i => i.count > 0);
    return (
        <div className="mt-8 pt-6 border-t border-slate-200" data-testid={`stats-prospects-source-${source.key}`}>
            <div className="flex items-center gap-2 mb-4">
                <Users size={17} className="text-emerald-600" />
                <p className="text-base font-semibold text-slate-800">Prospects créés — Source : {source.name}</p>
                <span className="ml-1 text-xl font-bold text-emerald-600">{source.total}</span>
            </div>
            {items.length === 0 ? (
                <p className="text-center text-slate-500 py-8 text-sm">Aucun prospect avec la source « {source.name} » sur la période choisie</p>
            ) : (
                <>
                    <div className="h-60 sm:h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={items} margin={{ top: 8, right: 16, bottom: 50, left: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis
                                    dataKey="label"
                                    angle={-25}
                                    textAnchor="end"
                                    interval={0}
                                    tick={{ fontSize: 11, fill: '#475569' }}
                                    height={60}
                                />
                                <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
                                <Tooltip
                                    cursor={{ fill: 'rgba(16,185,129,0.06)' }}
                                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                                    formatter={(value) => [`${value} (${pctOf(value, source.total)}%)`, 'Nombre']}
                                />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {items.map((entry, idx) => (
                                        <Cell key={entry.key} fill={colorFor(entry.key, idx)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3">
                        {items.map((entry, idx) => (
                            <div key={entry.key} className="flex items-center gap-2 text-sm">
                                <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: colorFor(entry.key, idx) }} />
                                <span className="text-slate-700">{entry.label}</span>
                                <span className="font-semibold text-slate-900">({entry.count} – {pctOf(entry.count, source.total)}%)</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function ProspectsStats({ period, setPeriod, dateDebut, setDateDebut, dateFin, setDateFin, data, loading }) {
    const items = (data?.items || []).filter(i => i.count > 0);
    const total = data?.total || 0;
    const byAssistante = data?.by_assistante || [];
    const bySource = data?.by_source || [];
    return (
        <Card className="rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="stats-prospects-panel">
            <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <Users size={20} className="text-[#2e7cd6]" />
                        <p className="text-lg font-semibold text-slate-800">Prospects créés</p>
                        <span className="ml-2 text-2xl font-bold text-[#2e7cd6]" data-testid="stats-prospects-total">{total}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-44 h-9 text-sm" data-testid="stats-period-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIOD_OPTIONS.map(o => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {period === 'custom' && (
                            <>
                                <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-date-debut" />
                                <span className="text-slate-400 text-sm">au</span>
                                <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-date-fin" />
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-500">
                        <Loader2 size={20} className="animate-spin mr-2" /> Chargement...
                    </div>
                ) : items.length === 0 ? (
                    <p className="text-center text-slate-500 py-16">Aucun prospect créé sur la période choisie</p>
                ) : (
                    <>
                        <div className="h-72 sm:h-96">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={items} margin={{ top: 8, right: 16, bottom: 50, left: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="label"
                                        angle={-25}
                                        textAnchor="end"
                                        interval={0}
                                        tick={{ fontSize: 11, fill: '#475569' }}
                                        height={60}
                                    />
                                    <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(46,124,214,0.06)' }}
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                                        formatter={(value) => [`${value} (${pctOf(value, total)}%)`, 'Nombre']}
                                    />
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                        {items.map((entry, idx) => (
                                            <Cell key={entry.key} fill={colorFor(entry.key, idx)} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3" data-testid="stats-legend">
                            {items.map((entry, idx) => (
                                <div key={entry.key} className="flex items-center gap-2 text-sm">
                                    <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: colorFor(entry.key, idx) }} />
                                    <span className="text-slate-700">{entry.label}</span>
                                    <span className="font-semibold text-slate-900">({entry.count} – {pctOf(entry.count, total)}%)</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                {!loading && byAssistante.map(a => (
                    <AssistanteProspectsChart key={a.id} assistante={a} />
                ))}
                {!loading && bySource.map(s => (
                    <SourceProspectsChart key={s.key} source={s} />
                ))}
            </CardContent>
        </Card>
    );
}

const ANCIEN_STATUT_LABEL = {
    sans_reponse_1: 'Sans réponse 1',
    sans_reponse_2: 'Sans réponse 2',
    sans_reponse_3: 'Sans réponse 3',
};

function RegagnesStats({ period, setPeriod, dateDebut, setDateDebut, dateFin, setDateFin, data, loading }) {
    const count = data?.count || 0;
    const items = data?.items || [];
    const fmtDate = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };
    return (
        <Card className="rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="stats-regagnes-panel">
            <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <TrendingUp size={20} className="text-amber-600" />
                        <p className="text-lg font-semibold text-slate-800">Prospects regagnés</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-44 h-9 text-sm" data-testid="stats-regagnes-period-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIOD_OPTIONS.map(o => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {period === 'custom' && (
                            <>
                                <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-regagnes-date-debut" />
                                <span className="text-slate-400 text-sm">au</span>
                                <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-regagnes-date-fin" />
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-500">
                        <Loader2 size={20} className="animate-spin mr-2" /> Chargement...
                    </div>
                ) : (
                    <>
                        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-5 flex items-center gap-4 mb-6 max-w-md">
                            <div className="w-12 h-12 rounded-lg bg-white border border-amber-300 flex items-center justify-center">
                                <TrendingUp size={24} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide font-semibold text-amber-700">Prospects regagnés</p>
                                <p className="text-3xl font-bold text-slate-800 leading-none mt-1" data-testid="stats-regagnes-count">{count}</p>
                                <p className="text-xs text-slate-500 mt-1">"Sans réponse" → "Visite"</p>
                            </div>
                        </div>
                        {items.length === 0 ? (
                            <p className="text-center text-slate-500 py-10">Aucun prospect regagné sur cette période</p>
                        ) : (
                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm" data-testid="stats-regagnes-table">
                                    <thead className="bg-slate-50 text-slate-600">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">Client</th>
                                            <th className="text-left px-3 py-2 font-medium">Ancien statut</th>
                                            <th className="text-left px-3 py-2 font-medium">Passé en "Visite" le</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map(it => (
                                            <tr key={it.prospect_id} className="border-t border-slate-100">
                                                <td className="px-3 py-2 text-slate-800">{it.client_nom || '-'}</td>
                                                <td className="px-3 py-2 text-slate-500">{ANCIEN_STATUT_LABEL[it.ancien_statut] || it.ancien_statut}</td>
                                                <td className="px-3 py-2 text-slate-500">{fmtDate(it.changed_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}


function TauxStats({ period, setPeriod, dateDebut, setDateDebut, dateFin, setDateFin, data, loading }) {
    const total = data?.total_dossiers || 0;
    const convertis = data?.dossiers_convertis || 0;
    const taux = data?.taux ?? 0;
    const items = data?.items || [];
    const fmtMoney = (v) => (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtDate = (iso) => {
        if (!iso) return '-';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };
    return (
        <Card className="rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="stats-taux-panel">
            <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <Percent size={20} className="text-violet-600" />
                        <p className="text-lg font-semibold text-slate-800">Taux de conversion</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-44 h-9 text-sm" data-testid="stats-taux-period-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIOD_OPTIONS.map(o => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {period === 'custom' && (
                            <>
                                <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-taux-date-debut" />
                                <span className="text-slate-400 text-sm">au</span>
                                <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-taux-date-fin" />
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-500">
                        <Loader2 size={20} className="animate-spin mr-2" /> Chargement...
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-slate-300 flex items-center justify-center">
                                    <Users size={24} className="text-slate-600" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-slate-700">Dossiers avec devis</p>
                                    <p className="text-3xl font-bold text-slate-800 leading-none mt-1" data-testid="stats-taux-total">{total}</p>
                                </div>
                            </div>
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-emerald-300 flex items-center justify-center">
                                    <CheckCircle2 size={24} className="text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-emerald-700">Dossiers convertis</p>
                                    <p className="text-3xl font-bold text-slate-800 leading-none mt-1" data-testid="stats-taux-convertis">{convertis}</p>
                                </div>
                            </div>
                            <div className="rounded-lg border border-violet-300 bg-violet-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-violet-400 flex items-center justify-center">
                                    <Percent size={24} className="text-violet-600" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-violet-700">Taux de conversion</p>
                                    <p className="text-3xl font-bold text-violet-700 leading-none mt-1" data-testid="stats-taux-pct">
                                        {taux.toFixed(1)} %
                                    </p>
                                </div>
                            </div>
                        </div>
                        {items.length === 0 ? (
                            <p className="text-center text-slate-500 py-10">Aucun dossier avec devis créé sur cette période</p>
                        ) : (
                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm" data-testid="stats-taux-table">
                                    <thead className="bg-slate-50 text-slate-600">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">Client</th>
                                            <th className="text-left px-3 py-2 font-medium">N° devis</th>
                                            <th className="text-left px-3 py-2 font-medium">Formule</th>
                                            <th className="text-center px-3 py-2 font-medium">Statut</th>
                                            <th className="text-right px-3 py-2 font-medium">Montant TTC</th>
                                            <th className="text-left px-3 py-2 font-medium">Accepté le</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map(it => (
                                            <tr key={it.inventaire_id} className="border-t border-slate-100">
                                                <td className="px-3 py-2 text-slate-800">{it.client_nom || '-'}</td>
                                                <td className="px-3 py-2 text-slate-500">{it.numero || '-'}</td>
                                                <td className="px-3 py-2 text-slate-500 capitalize">{it.formule || '-'}</td>
                                                <td className="px-3 py-2 text-center">
                                                    {it.converti ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                                            <CheckCircle2 size={12} /> Converti
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                                                            En attente
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-right font-medium text-slate-800">
                                                    {it.converti ? `${fmtMoney(it.prix_ttc)} €` : '-'}
                                                </td>
                                                <td className="px-3 py-2 text-slate-500">{it.converti ? fmtDate(it.accepted_at) : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}


// Couleurs par défaut si un commercial n'a pas de couleur configurée
const COMMERCIAL_FALLBACK_COLORS = ['#f97316', '#3b82f6', '#10b981', '#ec4899', '#8b5cf6', '#eab308', '#06b6d4', '#ef4444'];

function CommercialStats({ period, setPeriod, dateDebut, setDateDebut, dateFin, setDateFin, data, loading }) {
    const items = data?.items || [];
    // Préparer les données pour le bar chart
    const chartData = items.map((it, idx) => ({
        ...it,
        color: it.couleur || COMMERCIAL_FALLBACK_COLORS[idx % COMMERCIAL_FALLBACK_COLORS.length],
    }));

    return (
        <Card className="rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="stats-commercial-panel">
            <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <UserCheck size={20} className="text-orange-600" />
                        <p className="text-lg font-semibold text-slate-800">Conversion chaque commercial</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-44 h-9 text-sm" data-testid="stats-commercial-period-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIOD_OPTIONS.map(o => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {period === 'custom' && (
                            <>
                                <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-commercial-date-debut" />
                                <span className="text-slate-400 text-sm">au</span>
                                <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="w-40 h-9 text-sm" data-testid="stats-commercial-date-fin" />
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-500">
                        <Loader2 size={20} className="animate-spin mr-2" /> Chargement...
                    </div>
                ) : items.length === 0 ? (
                    <p className="text-center text-slate-500 py-16">Aucune activité commerciale sur cette période</p>
                ) : (
                    <>
                        <div className="h-80 sm:h-96 mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 12, right: 16, bottom: 12, left: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="commercial_name"
                                        tick={{ fontSize: 12, fill: '#475569' }}
                                        interval={0}
                                    />
                                    <YAxis
                                        domain={[0, 100]}
                                        tickFormatter={(v) => `${v}%`}
                                        tick={{ fontSize: 11, fill: '#475569' }}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(249,115,22,0.06)' }}
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                                        formatter={(value, name, props) => {
                                            const it = props?.payload;
                                            return [
                                                `${value} %  (${it?.dossiers_convertis}/${it?.total_dossiers})`,
                                                'Taux de conversion',
                                            ];
                                        }}
                                    />
                                    <Bar dataKey="taux" radius={[6, 6, 0, 0]}>
                                        {chartData.map((entry) => (
                                            <Cell key={entry.commercial_id || 'none'} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="border border-slate-200 rounded-lg overflow-x-auto">
                            <table className="w-full text-sm" data-testid="stats-commercial-table">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium whitespace-nowrap">Commercial</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Dossiers</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Convertis</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Taux</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap text-red-700">Refusé</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap text-amber-700">Intéressé</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Relance 1</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Relance 2</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Relance 3</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Envoyé</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">À envoyer</th>
                                        <th className="text-right px-3 py-2 font-medium whitespace-nowrap text-slate-500">Sans réponse</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chartData.map(it => {
                                        const bs = it.by_statut || {};
                                        const pct = (k) => it.total_dossiers ? ((bs[k] || 0) / it.total_dossiers * 100) : 0;
                                        const fmt = (k) => (bs[k] || 0) > 0
                                            ? <span title={`${bs[k]} dossier(s)`}>{pct(k).toFixed(1)} %</span>
                                            : <span className="text-slate-300">0 %</span>;
                                        return (
                                            <tr key={it.commercial_id || 'none'} className="border-t border-slate-100">
                                                <td className="px-3 py-2 text-slate-800 whitespace-nowrap">
                                                    <span className="inline-flex items-center gap-2">
                                                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: it.color }} />
                                                        {it.commercial_name}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{it.total_dossiers}</td>
                                                <td className="px-3 py-2 text-right text-emerald-700 font-medium whitespace-nowrap">{it.dossiers_convertis}</td>
                                                <td className="px-3 py-2 text-right font-semibold whitespace-nowrap" style={{ color: it.color }}>
                                                    {it.taux.toFixed(1)} %
                                                </td>
                                                <td className="px-3 py-2 text-right text-red-700 whitespace-nowrap">{fmt('refuse')}</td>
                                                <td className="px-3 py-2 text-right text-amber-700 whitespace-nowrap">{fmt('interesse')}</td>
                                                <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{fmt('relance_1')}</td>
                                                <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{fmt('relance_2')}</td>
                                                <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{fmt('relance_3')}</td>
                                                <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{fmt('envoye')}</td>
                                                <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{fmt('a_envoyer')}</td>
                                                <td className="px-3 py-2 text-right text-slate-500 whitespace-nowrap">{fmt('sans_reponse')}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function ChiffreAffairesStats({ year, setYear, data, loading }) {
    const items = data?.items || [];
    const totalYear = data?.total_year || 0;
    const totalNonProg = data?.total_non_programme || 0;
    const fmtMoney = (v) => (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let y = currentYear + 1; y >= currentYear - 4; y--) yearOptions.push(y);
    return (
        <Card className="rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="stats-ca-panel">
            <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <Wallet size={20} className="text-emerald-700" />
                        <p className="text-lg font-semibold text-slate-800">Chiffre d'affaires {year}</p>
                    </div>
                    <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                        <SelectTrigger className="w-32 h-9 text-sm" data-testid="stats-ca-year-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {yearOptions.map(y => (
                                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <p className="text-xs text-slate-500 mb-4 italic">
                    Basé sur la <strong>liste de facturation</strong> — date de déménagement (TTC). En rouge : dossiers facturés mais <strong>non programmés</strong>.
                </p>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-500">
                        <Loader2 size={20} className="animate-spin mr-2" /> Chargement...
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            <div className="rounded-lg border border-emerald-300 bg-emerald-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-emerald-400 flex items-center justify-center">
                                    <Wallet size={24} className="text-emerald-700" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-emerald-800">CA total {year}</p>
                                    <p className="text-3xl font-bold text-emerald-700 leading-none mt-1" data-testid="stats-ca-total">
                                        {fmtMoney(totalYear)} €
                                    </p>
                                </div>
                            </div>
                            <div className="rounded-lg border border-red-300 bg-red-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-red-400 flex items-center justify-center">
                                    <Wallet size={24} className="text-red-600" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-red-700">CA non programmé</p>
                                    <p className="text-3xl font-bold text-red-600 leading-none mt-1" data-testid="stats-ca-non-prog">
                                        {fmtMoney(totalNonProg)} €
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="h-80 sm:h-96 mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={items} margin={{ top: 12, right: 16, bottom: 12, left: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} interval={0} />
                                    <YAxis tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => `${(v / 1000).toLocaleString('fr-FR')}k`} />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(16,185,129,0.06)' }}
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                                        formatter={(value) => [
                                            `${(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })} €`,
                                            'CA TTC',
                                        ]}
                                    />
                                    <Bar dataKey="total_ttc" radius={[6, 6, 0, 0]}>
                                        {items.map((entry, idx) => (
                                            <Cell key={`cell-${idx}`} fill={entry.is_non_prog_col ? '#ef4444' : '#10b981'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex items-center justify-center gap-6 text-xs text-slate-600">
                            <span className="inline-flex items-center gap-2">
                                <span className="w-3 h-3 rounded-sm bg-emerald-500" /> CA mensuel programmé
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="w-3 h-3 rounded-sm bg-red-500" /> CA non programmé (cumulé année)
                            </span>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}


function CaCommercialStats({ year, setYear, data, loading }) {
    const items = data?.items || [];
    const commerciaux = data?.commerciaux || [];
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let y = currentYear + 1; y >= currentYear - 4; y--) yearOptions.push(y);

    // Totaux par commercial (programme + non programme)
    const totauxParComm = commerciaux.map(c => {
        const cid = c.commercial_id;
        let total = 0;
        for (const it of items) total += (it[cid] || 0);
        return { ...c, total };
    });
    const grandTotal = totauxParComm.reduce((s, c) => s + c.total, 0);
    const fmtMoney = (v) => (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    return (
        <Card className="rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="stats-ca-comm-panel">
            <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <Banknote size={20} className="text-teal-700" />
                        <p className="text-lg font-semibold text-slate-800">CA par commercial {year}</p>
                    </div>
                    <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                        <SelectTrigger className="w-32 h-9 text-sm" data-testid="stats-ca-comm-year-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {yearOptions.map(y => (
                                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <p className="text-xs text-slate-500 mb-4 italic">
                    Basé sur la <strong>liste de facturation</strong> — date de déménagement, agrégé par commercial. La 13ème colonne regroupe le CA <strong>non programmé</strong> cumulé sur l'année.
                </p>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-500">
                        <Loader2 size={20} className="animate-spin mr-2" /> Chargement...
                    </div>
                ) : commerciaux.length === 0 ? (
                    <p className="text-center text-slate-500 py-10">Aucune facture attribuée à un commercial pour {year}</p>
                ) : (
                    <>
                        <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                            <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">CA total {year} :</span>
                            <span className="text-2xl font-bold text-teal-700" data-testid="stats-ca-comm-total">
                                {fmtMoney(grandTotal)} €
                            </span>
                        </div>

                        <div className="h-96 mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={items} margin={{ top: 12, right: 16, bottom: 12, left: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} interval={0} />
                                    <YAxis tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => `${(v / 1000).toLocaleString('fr-FR')}k`} />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(20,184,166,0.06)' }}
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                                        formatter={(value, name) => {
                                            const c = commerciaux.find(x => x.commercial_id === name);
                                            return [`${(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })} €`, c?.name || name];
                                        }}
                                    />
                                    {commerciaux.map(c => (
                                        <Bar
                                            key={c.commercial_id}
                                            dataKey={c.commercial_id}
                                            fill={c.couleur}
                                            radius={[4, 4, 0, 0]}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        {/* Légende */}
                        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600 mb-4">
                            {commerciaux.map(c => (
                                <span key={c.commercial_id} className="inline-flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: c.couleur }} />
                                    {c.name}
                                </span>
                            ))}
                        </div>
                        {/* Tableau récap */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm" data-testid="stats-ca-comm-table">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium">Commercial</th>
                                        <th className="text-right px-3 py-2 font-medium">CA total {year}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...totauxParComm].sort((a, b) => b.total - a.total).map(c => (
                                        <tr key={c.commercial_id} className="border-t border-slate-100">
                                            <td className="px-3 py-2 text-slate-800">
                                                <span className="inline-flex items-center gap-2">
                                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.couleur }} />
                                                    {c.name}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right font-semibold text-slate-800">
                                                {fmtMoney(c.total)} €
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}


const MONTH_LABELS_LONG = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function CaSourceStats({ year, setYear, month, setMonth, data, loading }) {
    const items = data?.items || [];
    const total = data?.total || 0;
    const count = data?.count || 0;
    const fmtMoney = (v) => (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    // Navigation : on autorise tous les mois >= Mai 2026
    const isBefore = (y, m) => y < 2026 || (y === 2026 && m < 5);
    const prev = () => {
        let y = year, m = month - 1;
        if (m < 1) { m = 12; y -= 1; }
        if (isBefore(y, m)) return;
        setYear(y); setMonth(m);
    };
    const next = () => {
        let y = year, m = month + 1;
        if (m > 12) { m = 1; y += 1; }
        setYear(y); setMonth(m);
    };
    const canPrev = !isBefore(year, month - 1 < 1 ? 12 : month - 1);

    // Sélecteurs déroulants
    const currentY = new Date().getFullYear();
    const yearOptions = [];
    for (let y = 2026; y <= currentY + 2; y++) yearOptions.push(y);
    const monthOptions = MONTH_LABELS_LONG.map((lbl, i) => ({ value: i + 1, label: lbl }));

    return (
        <Card className="rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="stats-ca-source-panel">
            <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2">
                        <Compass size={20} className="text-indigo-600" />
                        <p className="text-lg font-semibold text-slate-800">CA par source — {MONTH_LABELS_LONG[month - 1]} {year}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={prev} disabled={!canPrev} data-testid="stats-ca-src-prev">
                            <ArrowLeft size={16} />
                        </Button>
                        <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
                            <SelectTrigger className="w-32 h-9 text-sm" data-testid="stats-ca-src-month-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {monthOptions.map(o => (
                                    <SelectItem key={o.value} value={String(o.value)} disabled={isBefore(year, o.value)}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                            <SelectTrigger className="w-24 h-9 text-sm" data-testid="stats-ca-src-year-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map(y => (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={next} data-testid="stats-ca-src-next">
                            <ArrowLeft size={16} className="rotate-180" />
                        </Button>
                    </div>
                </div>

                <p className="text-xs text-slate-500 mb-4 italic">
                    Source = canal d'acquisition de l'inventaire du dossier facturé. Premier mois affichable : <strong>Mai 2026</strong>.
                </p>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-500">
                        <Loader2 size={20} className="animate-spin mr-2" /> Chargement...
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            <div className="rounded-lg border border-indigo-300 bg-indigo-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-indigo-400 flex items-center justify-center">
                                    <Wallet size={24} className="text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-indigo-700">CA total du mois</p>
                                    <p className="text-3xl font-bold text-indigo-700 leading-none mt-1" data-testid="stats-ca-src-total">
                                        {fmtMoney(total)} €
                                    </p>
                                </div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-5 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-white border border-slate-300 flex items-center justify-center">
                                    <BarChart3 size={24} className="text-slate-600" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wide font-semibold text-slate-700">Nb factures</p>
                                    <p className="text-3xl font-bold text-slate-800 leading-none mt-1" data-testid="stats-ca-src-count">{count}</p>
                                </div>
                            </div>
                        </div>

                        {items.length === 0 ? (
                            <p className="text-center text-slate-500 py-10">Aucune facture en {MONTH_LABELS_LONG[month - 1]} {year}</p>
                        ) : (
                            <>
                                <div className="h-72 sm:h-80 mb-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={items} margin={{ top: 12, right: 16, bottom: 12, left: 8 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis dataKey="source" tick={{ fontSize: 11, fill: '#475569' }} interval={0} />
                                            <YAxis tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => `${(v / 1000).toLocaleString('fr-FR')}k`} />
                                            <Tooltip
                                                cursor={{ fill: 'rgba(99,102,241,0.06)' }}
                                                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                                                formatter={(value) => [`${(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })} €`, 'CA TTC']}
                                            />
                                            <Bar dataKey="ttc" radius={[6, 6, 0, 0]}>
                                                {items.map((entry) => (
                                                    <Cell key={entry.source} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="border border-slate-200 rounded-lg overflow-hidden">
                                    <table className="w-full text-sm" data-testid="stats-ca-src-table">
                                        <thead className="bg-slate-50 text-slate-600">
                                            <tr>
                                                <th className="text-left px-3 py-2 font-medium">Source</th>
                                                <th className="text-right px-3 py-2 font-medium">CA TTC</th>
                                                <th className="text-right px-3 py-2 font-medium">Part</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map(it => (
                                                <tr key={it.source} className="border-t border-slate-100">
                                                    <td className="px-3 py-2 text-slate-800">
                                                        <span className="inline-flex items-center gap-2">
                                                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: it.color }} />
                                                            {it.source}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-semibold text-slate-800">{fmtMoney(it.ttc)} €</td>
                                                    <td className="px-3 py-2 text-right text-slate-500">{total ? Math.round((it.ttc / total) * 100) : 0} %</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

